import { IEventSystem } from "@dhx/ts-common/events";
import { SelectionEvents, ISelectionEventsHandlersMap } from "@dhx/ts-common/types";
import { DataCollection, DataEvents, IDataEventsHandlersMap } from "@dhx/ts-data";
import { ISelectionConfig, ISelection } from "./types";

export class Selection implements ISelection {
	public config: ISelectionConfig;
	public events: IEventSystem<SelectionEvents | DataEvents, ISelectionEventsHandlersMap & IDataEventsHandlersMap>;

	private _selected: string[];
	private _data: DataCollection;
	private _lastSelectedIndex: number;
	private _lastShiftSelectedIndexes: number[];

	constructor(config: ISelectionConfig, data: DataCollection) {
		this.config = config;
		this.events = data.events as IEventSystem<SelectionEvents | DataEvents>;
		this._data = data;

		this._selected = [];
		this._lastShiftSelectedIndexes = [];
		this._data.events.on(DataEvents.removeAll, () => {
			this._selected = [];
		});
		this._data.events.on(DataEvents.afterRemove, obj => {
			this._selected = this._selected.filter(selectedId => selectedId !== obj.id);

			if (this.config.multiselection && this.getId().length !== 0) {
				return;
			}
			const id = this._data.getId(this._lastSelectedIndex);
			if (id) {
				this.add(id);
			} else {
				const lastItemId = this._data.getId(this._data.getLength() - 1);
				if (lastItemId) {
					this.add(lastItemId);
				}
			}
		});
	}

	getId(): string | string[] {
		if (this.config.multiselection) {
			return this._selected;
		}
		return this._selected[0];
	}

	getItem(): any {
		if (this._selected.length) {
			const items = this._selected.map(id => this._data.getItem(id));
			return this.config.multiselection ? items : items[0];
		}
		return null;
	}

	contains(id?: string): boolean {
		if (id) {
			return this._selected.indexOf(id) > -1;
		}
		return this._selected.length > 0;
	}

	remove(id?: string): boolean {
		if (!id) {
			this._data.map(item => {
				item.$selected = false;
				this._selected = [];
			});
			return;
		}
		if (!id && !this._selected.length) {
			return true;
		}
		if (id) {
			return this._unselectItem(id);
		}
		this._selected.forEach(selectedId => this._unselectItem(selectedId));
		return true;
	}

	add(id?: string, isCtrl?: boolean, isShift?: boolean) {
		if (!this.events.fire(SelectionEvents.beforeSelect)) {
			return;
		}
		if (!id) {
			this._selected = [];
			this._data.map(item => {
				item.$selected = true;
				this._selected.push(item.id);
			});
			return;
		}
		if (this.config.multiselection) {
			this._addMulti(id, isCtrl, isShift);
		} else {
			this._addSingle(id, isCtrl);
		}
	}

	private _addMulti(id?: string, isCtrl?: boolean, isShift?: boolean) {
		const currentSelectedItemIndex = this._data.getIndex(id);

		if (this.config.multiselectionMode === "click") {
			if (isShift) {
				this._addWithShift(currentSelectedItemIndex);
				this.events.fire(SelectionEvents.afterSelect, [id]);
			} else {
				this._isSelected(id) ? this._unselectItem(id) : this._selectItem(id);
				this._lastSelectedIndex = this._data.getIndex(this._selected[this._selected.length - 1]);
				this._lastShiftSelectedIndexes = [];
			}
		}

		if (this.config.multiselectionMode === "ctrlClick") {
			if (!isShift && !isCtrl) {
				this._data.map(item => {
					item.$selected = false;
					this._selected = [];
				});
				this._isSelected(id) ? this._unselectItem(id) : this._selectItem(id);
				this._lastSelectedIndex = this._data.getIndex(this._selected[this._selected.length - 1]);
				this._lastShiftSelectedIndexes = [];
			}
			if (isShift) {
				this._addWithShift(currentSelectedItemIndex);
				this.events.fire(SelectionEvents.afterSelect, [id]);
			}
			if (isCtrl) {
				this._isSelected(id) ? this._unselectItem(id) : this._selectItem(id);
				this._lastSelectedIndex = this._data.getIndex(this._selected[this._selected.length - 1]);
				this._lastShiftSelectedIndexes = [];
			}
		}
	}
	private _addWithShift(currentSelectedItemIndex: number) {
		if (currentSelectedItemIndex >= this._lastSelectedIndex) {
			// remove last selection with shift
			this._data.map((item, index) => {
				if (this._lastShiftSelectedIndexes.indexOf(index) !== -1) {
					item.$selected = false;
					this._selected = this._selected.filter(i => i !== this._data.getId(index));
					this._lastShiftSelectedIndexes = this._lastShiftSelectedIndexes.filter((i) => {
						return i !== index && i !== this._lastSelectedIndex;
					});
				}
			});
			// add new selection with shift
			this._data.map((item, index) => {
				if (index >= this._lastSelectedIndex && index <= currentSelectedItemIndex) {
					item.$selected = true;
					if (this._selected.indexOf(item.id) === -1) {
						this._selected.push(item.id);
					}
					if (index !== this._lastSelectedIndex) {
						if (this._lastShiftSelectedIndexes.indexOf(index) === -1) {
							this._lastShiftSelectedIndexes.push(index);
						}
					}
				}
			});
		}
		if (currentSelectedItemIndex <= this._lastSelectedIndex) {
			// remove last selection with shift
			this._data.map((item, index) => {
				if (this._lastShiftSelectedIndexes.indexOf(index) !== -1) {
					item.$selected = false;
					this._selected = this._selected.filter(i => i !== this._data.getId(index));
					this._lastShiftSelectedIndexes = this._lastShiftSelectedIndexes.filter((i) => {
						return i !== index && i !== this._lastSelectedIndex;
					});
				}
			});
			// add new selection with shift
			this._data.map((item, index) => {
				if (index <= this._lastSelectedIndex && index >= currentSelectedItemIndex) {
					item.$selected = true;
					if (this._selected.indexOf(item.id) === -1) {
						this._selected.push(item.id);
					}
					if (index !== this._lastSelectedIndex) {
						if (this._lastShiftSelectedIndexes.indexOf(index) === -1) {
							this._lastShiftSelectedIndexes.push(index);
						}
					}
				}
			});
		}
	}

	private _addSingle(id?: string, isCtrl?: boolean) {
		// clean selection
		this.remove();
		// select item
		if (this.config && this.config.multiselectionMode === "click") {
			this._selectItem(id);
		} else {
			if (isCtrl) {
				this._selectItem(id);
			}
		}
	}
	private _isSelected(id: string) {
		return this._selected.indexOf(id) !== -1;
	}
	private _selectItem(id: string) {
		this._selected.push(id);
		this._data.update(id, { $selected: true });
		this._lastSelectedIndex = this._data.getIndex(id);

		this.events.fire(SelectionEvents.afterSelect, [id]);
	}
	private _unselectItem(id: string) {
		if (this.events.fire(SelectionEvents.beforeUnSelect, [id])) {
			this._data.update(id, { $selected: false });
			this._selected = this._selected.filter(selectedId => selectedId !== id);
			this.events.fire(SelectionEvents.afterUnSelect, [id]);
			return true;
		}
		return false;
	}
}

